import { useCallback, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { FavoriteBrandItem } from '../slices/favorite-brand-slice';
import { dialogActions } from '../components/dialog/slice/dialog-slice';
import { apiActions } from '../slices/api-slice';
import { ApiFavoriteMovePost } from '../api/front/favorite/api-favorite';

type useFavoriteFolderDragAndDropParams = {
  kijshopCd: string;
  favoriteDataList: FavoriteBrandItem[];
  selectedId: string | undefined;
  content: FavoriteBrandItem;
  draggingList: FavoriteBrandItem[];
  setDraggingList: React.Dispatch<React.SetStateAction<FavoriteBrandItem[]>>,
  onUpdateLoading: (bool: boolean) => void;
  onSuccess: () => void;
  autoScrollHandlers: {
    onDragStart: () => void;
    onDragEnd: () => void;
    onDragOver: (e: React.DragEvent) => void;
  };
  // フォルダを開く操作
  onOpenFolder?: (folderId: string) => void;
  // 自動で開くまでの待機時間（ミリ秒）
  autoOpenDelay?: number;
  setDropOverItems: React.Dispatch<React.SetStateAction<FavoriteBrandItem[]>>,
};

export const useFavoriteFolderDragAndDrop = (params: useFavoriteFolderDragAndDropParams) => {
  const {
    kijshopCd,
    favoriteDataList,
    selectedId,
    content,
    draggingList,
    setDraggingList,
    onUpdateLoading,
    onSuccess,
    autoScrollHandlers,
    onOpenFolder,
    autoOpenDelay = 800, // デフォルト800ms
    setDropOverItems,
  } = params;
  const dispatch = useDispatch();
  const [isDragOver, setIsDragOver] = useState(false);

  // 自動オープン用のタイマーを保持
  const autoOpenTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastHoveredIdRef = useRef<string | null>(null);

  // 選択されたIDから親子関係の重複を除去
  const filterParentFirst = useCallback((ids: string[], allItems: FavoriteBrandItem[]): string[] => {
    const idSet = new Set(ids);
    const filterRecursive = (items: FavoriteBrandItem[], ancestorSelected: boolean) => {
      for (const item of items) {
        const isSelected = idSet.has(item.id);
        // 祖先が選択されている場合 → 自分は削除
        if (ancestorSelected && isSelected) {
          idSet.delete(item.id);
        }
        // 次の階層では、祖先がすでに選択済みか、自分自身が選択されているかを渡す
        const nextAncestorSelected = ancestorSelected || isSelected;
        if (item.children.length) {
          filterRecursive(item.children, nextAncestorSelected);
        }
      }
    };
    filterRecursive(allItems, false);
    return Array.from(idSet);
  }, []);

  // ドラッグしているidによって値を取得する処理
  const findMatchedItems = useCallback((list: FavoriteBrandItem[], ids: string[]) => {
    let result: FavoriteBrandItem[] = [];
    for (const item of list) {
      // 親が一致した場合
      if (ids.includes(item.id)) {
        result.push(item);
      }
      // 子がある場合は再帰的に探索
      if (item.children && item.children.length > 0) {
        const childMatches = findMatchedItems(item.children, ids);
        result = result.concat(childMatches);
      }
    }
    return result;
  }, []);

  // ドラッグするファイルが最大何階層あるか確認
  const getMaxDepth = useCallback((matchedItems: FavoriteBrandItem[]): number => {
    if (!matchedItems || matchedItems.length === 0) return 0;
    const getDepth = (v: FavoriteBrandItem): number => {
      if (!v.children || v.children.length === 0) return 1;
      const depths = v.children.map(child => getDepth(child));
      return 1 + Math.max(...depths);
    }
    const depths = matchedItems.map(v => getDepth(v));
    return Math.max(...depths);
  }, []);

  // タイマーをクリア
  const clearAutoOpenTimer = useCallback(() => {
    if (autoOpenTimerRef.current) {
      clearTimeout(autoOpenTimerRef.current);
      autoOpenTimerRef.current = null;
    }
  }, []);

  // ドラッグ開始
  const handleDragStart = useCallback((e: React.DragEvent<HTMLDivElement>, content: FavoriteBrandItem, selectedItems: FavoriteBrandItem[]) => {

    const getTarget = (items: FavoriteBrandItem[], id: string): FavoriteBrandItem | undefined => {
      for (const item of items) {
        if (item.id === id) return item;
        if (item.children?.length) {
          const hit = getTarget(item.children, id);
          if (hit) return hit;
        }
      }
      return undefined;
    };

    // 汎用型ガード
    const isDefined = <T,>(v: T | null | undefined): v is T => v != null;

    const dragIds = selectedItems.find(v => v.id === content.id) ? selectedItems : [content];

    // 重複があれば一応排除（順序維持）
    const uniqDragIds = Array.from(new Set(dragIds));
    const dragging = uniqDragIds
      .map(item => getTarget(favoriteDataList, item.id))
      .filter(isDefined);

    setDraggingList(dragging);

    autoScrollHandlers.onDragStart();

    // ドラッグ開始時はタイマーをクリア
    clearAutoOpenTimer();
    lastHoveredIdRef.current = null;
  }, [autoScrollHandlers, clearAutoOpenTimer, favoriteDataList, setDraggingList]);

  // ドラッグオーバー（フォルダ自動オープン機能付き）
  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>, targetContent?: FavoriteBrandItem) => {
    e.preventDefault();
    autoScrollHandlers.onDragOver(e);
    setIsDragOver(true);
    const getParent = (items: FavoriteBrandItem[], target: FavoriteBrandItem): FavoriteBrandItem | null => {
      for (const item of items) {
        if (item.id === target.parentId) {
          return item;
        }
        if (item.children.length) {
          const result = getParent(item.children, target);
          if (result) {
            return result;
          }
        }
      }
      return null;
    }

    const collectIds = (items: FavoriteBrandItem[], target: FavoriteBrandItem): FavoriteBrandItem[] => {

      if (!selectedId || items.find(v => (draggingList.find(drg => drg.parentId === v.parentId)) && v.type !== 'folder')) return [];
      return items.flatMap((v) => {
        const ids = [v];
        if (v.children && v.children.length > 0) {
          ids.push(...collectIds(v.children, target));
        }
        return ids;
      });
    };

    if (targetContent?.type === 'folder') {
      const allIds = collectIds([...targetContent.children, targetContent], targetContent);
      setDropOverItems(allIds);
    } else if (targetContent?.type === 'brand') {
      const parent = getParent(favoriteDataList, targetContent)
      const allItems = collectIds(parent?.children ?? [], targetContent);
      if (parent && !draggingList.find(drg => drg.parentId === parent.id)) allItems.push(parent);

      setDropOverItems(allItems);
    }
    // フォルダでない場合、または子要素がない場合は何もしない
    if (!targetContent || targetContent.type !== 'folder' || !targetContent.children.length) {
      clearAutoOpenTimer();
      lastHoveredIdRef.current = null;
      return;
    }

    // 同じフォルダの上にいる場合はタイマーを継続
    if (lastHoveredIdRef.current === targetContent.id) {
      return;
    }

    // 異なるフォルダに移動した場合、タイマーをリセット
    clearAutoOpenTimer();
    lastHoveredIdRef.current = targetContent.id;

    // 新しいタイマーを設定
    autoOpenTimerRef.current = setTimeout(() => {
      if (onOpenFolder && targetContent.type === 'folder') {
        onOpenFolder(targetContent.id);
      }
    }, autoOpenDelay);
  }, [autoScrollHandlers, clearAutoOpenTimer, onOpenFolder, autoOpenDelay, favoriteDataList, content, selectedId]);

  // ドロップ
  const handleDrop = useCallback(async (e: React.DragEvent<HTMLDivElement>, targetContent: FavoriteBrandItem) => {
    e.preventDefault();
    autoScrollHandlers.onDragEnd();

    // タイマーをクリア
    clearAutoOpenTimer();
    lastHoveredIdRef.current = null;

    setDropOverItems([]);
    let draggedIds = draggingList.map(v => v.id);
    if (!draggedIds.length || draggedIds.includes(targetContent.id)) return;

    const canDropHere = () => {
      if (!selectedId) return false;
      if (draggingList.some(v => v.parentId === targetContent.parentId) && targetContent.type !== 'folder') return false;
      if (draggingList.some(v => v.parentId === targetContent.id)) return false;
      return true;
    };
    if (!canDropHere()) {
      setDraggingList([]);
      return;
    }
    setDraggingList([]);
    // 親が含まれている場合は子を除外
    draggedIds = filterParentFirst(draggedIds, favoriteDataList);
    if (!draggedIds.length) return;

    onUpdateLoading(true);
    // 移動API実行
    dispatch(
      apiActions.run(
        new ApiFavoriteMovePost({ kijshopCd, srcIds: draggedIds, dstFolderId: targetContent.type === 'folder' ? targetContent.id : targetContent.parentId }),
        {
          onSuccess, onError: (e) => {
            onUpdateLoading(false);
            dispatch(dialogActions.pushMessage({
              title: '確認',
              message: e.messages,
              buttons: [{
                label: 'はい',
                callback: () => dispatch(dialogActions.pop()),
              }],
            }));
          }
        }
      )
    );
  }, [autoScrollHandlers, clearAutoOpenTimer, filterParentFirst, favoriteDataList, kijshopCd, onSuccess, draggingList, setDraggingList, selectedId, onUpdateLoading, dispatch, setDropOverItems]);

  // ドラッグ終了
  const handleDragEnd = useCallback(() => {
    autoScrollHandlers.onDragEnd();
    clearAutoOpenTimer();
    lastHoveredIdRef.current = null;
  }, [autoScrollHandlers, clearAutoOpenTimer]);

  // ドラッグ離脱（フォルダから離れた時）
  const handleDragLeave = useCallback(() => {
    clearAutoOpenTimer();
    setIsDragOver(false);
    lastHoveredIdRef.current = null;
  }, [clearAutoOpenTimer]);

  return {
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleDragEnd,
    handleDragLeave,
    isDragOver,
  };
};
